import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiFileText, FiCalendar, FiAlertCircle } from 'react-icons/fi';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import Timeline from './admin/component/Timeline';
import '../styles/global.css';

function JobDescriptions({ adminToken, onLogout }) {
  const { i18n } = useTranslation();
  const isRTL = document.documentElement.dir === 'rtl' || i18n.language === 'ar';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apiError, setApiError] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);

  // const handleLogout = () => {
  //   if (onLogout) {
  //     onLogout();
  //   } else {
  //     logout();
  //     navigate('/login');
  //   }
  // };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        setApiError(false);
        const response = await api.get('jobs/');
        setJobs(response.data.results || response.data);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        if (err.message === 'Network Error' || !err.response) {
          setApiError(true);
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        } else {
          setError('Failed to load job descriptions. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <>
      <div className="container" style={{ maxWidth: '1000px', padding: '2rem' }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 className="mb-2">Job Descriptions</h1>
              <p className="text-muted mb-0">
                Browse through available job positions and their requirements.
              </p>
            </div>
          </motion.div>

          {apiError && (
            <motion.div
              className="alert alert-error mb-4 d-flex align-items-center gap-2"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ backgroundColor: 'var(--error-100)', color: 'var(--error-700)', border: '1px solid var(--error-200)' }}
            >
              <FiAlertCircle size={20} />
              <div>
                <strong>Connection Error</strong>
                <p className="m-0">{error}</p>
              </div>
            </motion.div>
          )}

          {error && !apiError && (
            <motion.div
              className="alert alert-error mb-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}

          {loading ? (
            <div className="text-center p-4">
              <div className="spinner"></div>
              <p>Loading job descriptions...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center p-4 card">
              <FiFileText size={48} style={{ color: 'var(--neutral-400)', marginBottom: '1rem' }} />
              <p>No job descriptions available at the moment.</p>
            </div>
          ) : (
            <div className="row">
              {jobs.map((job) => (
                <div className="col-md-6 mb-4" key={job.id}>
                  <motion.div
                    className="card h-100"
                    variants={itemVariants}
                    whileHover={{ y: -5, boxShadow: 'var(--shadow-lg)' }}
                  >
                    <div className="card-body">
                      <h5 className="card-title">{job.title}</h5>
                      <p className="card-text text-muted mb-3">
                        <FiCalendar className="me-1" /> Posted on {formatDate(job.created_at)}
                      </p>
                      <p className="card-text">{job.description}</p>
                      <div className="mt-3">
                        <h6>Requirements:</h6>
                        <ul className="list-unstyled">
                          {job.requirements?.map((req, index) => (
                            <li key={index} className="mb-1">• {req}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Timeline Drawer */}
      <Timeline
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        apiEndpoint="audit/timeline/job_descriptions/"
        title="Job Descriptions Timeline"
        pageName="Job Descriptions"
        width={450}
      />

      {/* Fixed Vertical Timeline Button - Center Right/Left based on RTL */}
      <button
        onClick={() => setTimelineOpen(true)}
        className={`fixed ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 z-40 flex items-center justify-center text-white p-2 rounded-full transition-all duration-200 group hover:scale-110`}
        style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)'
        }}
      >
        <Clock size={14} />
        <span className={`absolute ${isRTL ? 'left-full ml-3' : 'right-full mr-3'} px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none`}>
          Timeline
        </span>
      </button>
    </>
  );
}

export default JobDescriptions; 